import SeoLandingTemplate from "./SeoLandingTemplate";

export default function EventRegistrationBelgium() {
  return (
    <SeoLandingTemplate
      keyword="Event Registration Belgium"
      path="/event-registration-belgium"
      title="Event Registration Belgium — Branded Online & Onsite | OneTec"
      description="Event registration in Belgium since 2002. Branded online sign-up flows, payment integration, GDPR-safe data capture, automated reminders and onsite badging at scale."
      serviceType="Event Registration"
      hero={{
        eyebrow: "Service · Event Technology",
        headline: (
          <>
            Event registration
            <br />
            <span className="bg-ot-peach border-2 border-ot-fg px-2">in Belgium.</span>
          </>
        ),
        subhead:
          "From small private dinners in Brussels to international congresses in Zaventem — OneTec builds branded event registration platforms with payments, GDPR-safe data capture, automated reminders and seamless onsite check-in.",
      }}
      intro={[
        "Most agencies underestimate event registration. They treat it as a checkbox tool — until 800 attendees show up at 8:55 a.m. and the sign-up page crashes. We don't. We've been running event registration in Belgium since 2002, for global brands and for 50-seat board meetings, and we know that the registration flow is your event's first impression.",
        "OneTec's event registration platform combines a fully branded sign-up experience (your colors, your typography, your tone — no third-party logos), GDPR-compliant data capture compliant with Belgian and EU regulations, automated confirmation and reminder emails in EN/FR/NL, payment integration (Stripe, Mollie, Bancontact), and a live dashboard so your client can watch numbers grow in real time.",
        "And because we're a Belgian event technology company headquartered in Zaventem, we connect the registration data to our onsite badging kiosks: when an attendee scans their QR code at the door, their PVC or paper badge prints in under 3 seconds. No queues. No fumbling. No \"sorry, we can't find your name.\"",
      ]}
      benefits={[
        {
          title: "Fully branded",
          body:
            "Your event registration pages are designed pixel-perfect to your brand guidelines. No generic platform UI, no third-party banners. Hosted on your domain.",
        },
        {
          title: "GDPR-native",
          body:
            "Built by a Belgian company under Belgian law. Privacy-by-design, granular consent capture, EU data centers, exportable audit logs.",
        },
        {
          title: "Multi-language",
          body:
            "Sign-up flows in EN, FR, NL, DE — auto-detected or user-selected. Automated emails in the attendee's language.",
        },
        {
          title: "Payments included",
          body:
            "Stripe, Mollie, Bancontact, credit cards, SEPA. Multiple ticket tiers, promo codes, group bookings, VAT-compliant invoicing.",
        },
        {
          title: "Connected to onsite",
          body:
            "QR/RFID-coded confirmation emails, instant kiosk check-in, on-demand badge printing — 1000+ attendees/hour.",
        },
        {
          title: "Live dashboards",
          body:
            "Real-time registration counters, demographic breakdowns, exportable CSV, white-label access for your client.",
        },
      ]}
      features={[
        "Custom-branded landing & registration pages",
        "Stripe / Mollie / Bancontact payment integration",
        "Multi-ticket logic, promo codes, group registrations",
        "Automated confirmation, reminder & survey emails (EN/FR/NL/DE)",
        "GDPR-compliant data capture & exportable consent logs",
        "QR / RFID code generation per attendee",
        "Real-time dashboards & demographic reports",
        "Onsite check-in kiosks & PVC/paper badge printing",
        "API integration with your CRM (HubSpot, Salesforce, Mailchimp)",
        "Dedicated project manager for every event",
      ]}
      useCases={[
        { title: "Corporate Congresses", body: "Multi-day, multi-track, multi-language registrations." },
        { title: "Product Launches", body: "Press, VIP and public tiers with separate flows." },
        { title: "Trade Shows", body: "Exhibitor + visitor flows, lead-tracking integrated." },
        { title: "AGMs & Boards", body: "Secure private registration with voting integration." },
      ]}
      faqs={[
        {
          q: "Do you handle event registration for events outside Belgium?",
          a: "Yes. While we are headquartered in Zaventem, Belgium, we deliver event registration and onsite services across the EU and beyond — 42 countries to date. Our platform is multi-language and our team travels.",
        },
        {
          q: "Is your event registration platform GDPR-compliant?",
          a: "Yes. OneTec is a Belgian SRL operating under EU law. Our event registration platform is built privacy-by-design: granular consent capture, EU-only data centers, deletion on request, and full audit logs. You can read our full Privacy Policy on this site.",
        },
        {
          q: "How fast can you set up event registration?",
          a: "A branded registration page with payments and confirmation emails can go live in 5 working days. Complex multi-tier or multi-language events typically take 2-3 weeks including testing.",
        },
        {
          q: "Can event registration integrate with onsite badging?",
          a: "Absolutely — it's our specialty. Every registration generates a unique QR/RFID code that prints an on-demand badge at our self-check-in kiosks. We process over 1000 attendees per hour at peak.",
        },
        {
          q: "Do you support payments in Belgium (Bancontact, SEPA)?",
          a: "Yes. We integrate Mollie and Stripe, which support Bancontact, SEPA, iDEAL, credit cards, and standard EU payment methods. VAT-compliant invoicing included.",
        },
      ]}
      relatedProducts={[
        { slug: "online-registration", name: "Online Registration" },
        { slug: "onsite-registration-badging", name: "Onsite Registration & Badging" },
        { slug: "access-control", name: "Access Control" },
        { slug: "lead-trackers", name: "Lead Trackers" },
      ]}
    />
  );
}
